import { config } from "@/config";
import Sentry from "@/integrations/Sentry";
import { mirrorShareAuth } from "@/native/shareAuthMirror";
import { queryClient } from "@/queryClient";
import { Auth0ContextInterface, useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { useEffect } from "react";
import { handleAxiosError } from "./utils";

import { wait } from "@/utils/wait";
import { CohesiveAPIClient, MeData } from "@cohesive/api-client";
import { auth0Config } from "../auth0.config";
import { getCanvasCaptureBearerToken } from "./canvasCaptureAuth";

/** `canvas-capture.html` entry: no Auth0; orgAPI uses capture access JWT from inject or URL. */
export function CaptureOnlyApiInject() {
  useEffect(() => {
    orgAPI.setGetToken(async () => {
      const capture = getCanvasCaptureBearerToken();
      if (!capture) {
        throw new Error("Canvas capture token not set");
      }
      return capture;
    });
    authInterceptor.setAuthGetter(null);
  }, []);

  return null;
}

export const api = axios.create({
  baseURL: config.hosts.api,
});

/**
 * auth0-spa-js error codes that mean the session is over: no amount of retrying
 * produces a token, only a fresh login does. `missing_refresh_token` is the one
 * that bites on native — `useRefreshTokensFallback` is off there (no iframe), so
 * a cache entry without a refresh token is terminal rather than recoverable.
 */
const TERMINAL_AUTH_ERRORS = new Set([
  "missing_refresh_token",
  "invalid_grant",
  "login_required",
  "consent_required",
]);

function isTerminalAuthError(e: unknown): boolean {
  const code = (e as { error?: unknown } | null)?.error;
  return typeof code === "string" && TERMINAL_AUTH_ERRORS.has(code);
}

class AuthInterceptor {
  getToken: Auth0ContextInterface["getAccessTokenSilently"] | null = null;

  setAuthGetter(
    getToken: Auth0ContextInterface["getAccessTokenSilently"] | null,
  ) {
    this.getToken = getToken;
  }

  constructor() {
    this.intercept = this.intercept.bind(this);
    this.setAuthGetter = this.setAuthGetter.bind(this);
  }

  async intercept(config: any) {
    if (!config.headers.Authorization) {
      const captureToken = getCanvasCaptureBearerToken();
      if (captureToken) {
        config.headers["Authorization"] = `Bearer ${captureToken}`;
        return config;
      }

      let token = null;

      // Bounded retry: getToken can transiently be unset (during Auth0 init) or
      // throw (e.g. expired refresh token). Previously this looped forever,
      // hanging every request — especially bad on native. Give up after ~5s and
      // reject so the response/auth layer can surface a re-login.
      //
      // 5s (not 3s) to leave headroom for a cold Auth0 init over a slow
      // connection: `checkSession()` does a network round trip, and a request
      // that fires before it lands would otherwise fail where it used to wait.
      // Queries get ~3 React Query retries on top of this; mutations get one
      // shot, so the budget has to cover init on its own.
      const maxAttempts = 100;
      let attempts = 0;

      while (!token && attempts < maxAttempts) {
        // Backoff between attempts, not before the first one: an unconditional
        // timer here put a macrotask between every request and its token, so a
        // request issued alongside a route change lost the main thread to that
        // render and left only once it had finished.
        if (attempts > 0) await wait(50);
        attempts++;
        try {
          token = await this.getToken?.();
        } catch (e) {
          // A dead session can never be retried into a live one. Bail out now
          // instead of burning the full ~3s budget on 60 doomed token calls —
          // each one takes the cross-tab Auth0 lock, so on native (where there
          // is no iframe fallback) this turned an expired session into a
          // multi-second freeze on every request.
          if (isTerminalAuthError(e)) throw e;
          console.error("failed to get token", e);
        }
      }

      if (!token) {
        throw new Error(
          "Unable to acquire access token; your session may have expired.",
        );
      }

      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  }
}

export function AuthInject() {
  const { getAccessTokenSilently, isLoading } = useAuth0();

  useEffect(() => {
    if (!isLoading) {
      authInterceptor.setAuthGetter(getAccessTokenSilently);
      // Keep the iOS Share Extension's Keychain mirror fresh (no-op off native).
      mirrorShareAuth(getAccessTokenSilently);
    }

    orgAPI.setGetToken(getAccessTokenSilently);

    return () => authInterceptor.setAuthGetter(null);
  }, [getAccessTokenSilently, isLoading]);

  return null;
}

export const authInterceptor = new AuthInterceptor();

export async function getToken() {
  return authInterceptor.getToken?.();
}

// Add auth interceptor to axios instance
api.interceptors.request.use(authInterceptor.intercept);

/**
 * Shared response error handler for both axios instances. A 402 from the
 * backend's usage gate when our cached MeData still says usage is allowed
 * means another user in the org just pushed it over the overage limit —
 * the cache is stale. Refresh /me so the gate UI catches up, and skip
 * Sentry: that race is expected. Other 402s (cache already says
 * disallowed, or no cached MeData) fall through to Sentry — they indicate
 * a request slipped past UI gating, which is worth capturing.
 */
function handleResponseError(error: unknown) {
  if (axios.isAxiosError(error) && error.response?.status === 402) {
    const userInfo = queryClient.getQueryData<MeData>(["userInfo"]);
    if (userInfo?.usage?.usage_allowed === true) {
      queryClient.invalidateQueries({ queryKey: ["userInfo"] });
      return Promise.reject(handleAxiosError(error));
    }
  }
  Sentry.captureException(error);
  return Promise.reject(handleAxiosError(error));
}

// Add error handling interceptor
api.interceptors.response.use((response) => response, handleResponseError);

// Create the organization API client (no @auth0/auth0-spa-js instance — avoids secure-origin
// failures on hosts like host.docker.internal; Auth0 React supplies tokens via setGetToken).
export const orgAPI = new CohesiveAPIClient({
  axiosOptions: {
    baseURL: config.hosts.api,
  },
  auth0Options: auth0Config,
  useAuth0Spa: false,
});

orgAPI.axios.interceptors.request.use(authInterceptor.intercept);
orgAPI.axios.interceptors.response.use(
  (response) => response,
  handleResponseError,
);
